import { useState, useEffect } from "react";
import { alpha } from "@mui/material/styles";


export interface ColorPalette {
    application: string;
    favicon: string;
    logo: string;
    muiPalette: {
        primary: {
            main: string;
            light: string;
            dark: string;
        };
        secondary: {
            main: string;
            light: string;
            dark: string;
        }
    };
    cssPalette: {
        "--primary-color": string;
        "--secondary-color": string;
        "--grad-one-color": string;
        "--grad-two-color": string;
        "--highlight-color": string;
        "--darken-color": string;
    }
};

export const colorPalettes: ColorPalette[] = [
    {
        application: 'assay-central',
        favicon: `favicon.ico`,
        logo: "assay-central.png",
        muiPalette: {
            primary: {
                main: '#006D77',
                light: alpha('#006D77', 0.2),
                dark: alpha('#006D77', 0.8),
            },
            secondary: {
                main: '#D5E7E8',
                light: alpha('#D5E7E8', 0.2),
                dark: alpha('#D5E7E8', 0.8),
            },
        },
        cssPalette: {
            "--primary-color": '0, 109, 119',
            "--secondary-color": '213, 231, 232',
            "--grad-one-color": '171, 206, 209',
            "--grad-two-color": '221, 231, 179',
            "--highlight-color": '26, 193, 163',
            "--darken-color": '0, 76, 83',
        },
    },
    {
        application: 'megatox',
        favicon: `favicon-mt.ico`,
        logo: "megatox.png",
        muiPalette: {
            primary: {
                main: '#10826D',
                light: alpha('#10826D', 0.2),
                dark: alpha('#10826D', 0.8),
            },
            secondary: {
                main: '#CBE6CA',
                light: alpha('#CBE6CA', 0.2),
                dark: alpha('#CBE6CA', 0.8),
            },
        },
        cssPalette: {
            "--primary-color": '16, 130, 108',
            "--secondary-color": '203, 230, 202',
            "--grad-one-color": '148, 205, 183',
            "--grad-two-color": '185, 227, 239',
            "--highlight-color": '34, 193, 135',
            "--darken-color": '0, 77, 58',
        },
    },
    {
        application: 'megatrans',
        favicon: `favicon-mtr.ico`,
        logo: "megatrans.png",
        muiPalette: {
            primary: {
                main: '#4A4846',
                light: alpha('#4A4846', 0.2),
                dark: alpha('#4A4846', 0.8),
            },
            secondary: {
                main: '#CDC0B1',
                light: alpha('#CDC0B1', 0.2),
                dark: alpha('#CDC0B1', 0.8),
            },
        },
        cssPalette: {
            "--primary-color": '74, 72, 70',
            "--secondary-color": '205, 192, 177',
            "--grad-one-color": '188, 183, 161',
            "--grad-two-color": '221, 200, 146',
            "--highlight-color": '218, 165, 32',
            "--darken-color": '37, 34, 31',
        },
    },
    {
        application: 'megaache',
        favicon: `favicon-mache.ico`,
        logo: "megaache.png",
        muiPalette: {
            primary: {
                main: '#003D69',
                light: alpha('#003D69', 0.2),
                dark: alpha('#003D69', 0.8),
            },
            secondary: {
                main: '#ABC7DC',
                light: alpha('#ABC7DC', 0.2),
                dark: alpha('#ABC7DC', 0.8),
            },
        },
        cssPalette: {
            "--primary-color": '0, 61, 105',
            "--secondary-color": '171, 199, 220',
            "--grad-one-color": '171, 199, 220',
            "--grad-two-color": '226, 201, 241',
            "--highlight-color": '161, 85, 243',
            "--darken-color": '0, 44, 75',
        },
    },
];

const useColorPalette = () => {
    const [ palette, setPalette ] = useState<ColorPalette>(colorPalettes[0]);

    
    useEffect(() => {
        const app = process.env.REACT_APP_APPLICATION || 'assay-central';
        const palette = colorPalettes.find(p => p.application === app) || colorPalettes[0];
        setPalette(palette);
    }, []);

    const setColorPalette = () => {
        const app = process.env.REACT_APP_APPLICATION || 'assay-central';
        const { cssPalette, favicon } = colorPalettes.find(p => p.application === app) || colorPalettes[0];
        
        // Sets css variables
        for (const [key, value] of Object.entries(cssPalette)) {
            document.documentElement.style.setProperty(key, value);
        };

        // Sets favicon
        const link = document.getElementById('favicon');
        if (link) {
            (link as HTMLLinkElement).href = `${process.env.PUBLIC_URL}/${favicon}`;
        }

        // Sets document title
        document.title = process.env.REACT_APP_META_TITLE || 'Assay Central';

        return app;
    }

    return {
        palette,
        setColorPalette,
    }
};

export default useColorPalette;